import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {
    BucketName,
    CurrencyRate,
    IncidentTransaction,
    ReferenceBookResponse,
    ReferenceBookType,
    RefundApiService,
    RefundResponse,
    RefundType,
} from '@npck/af-frontend-api';
import {DATE_SERVER_FORMAT, LocalizationService, rxSpinner, Spinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {combineLatest, EMPTY, mergeMap, of} from 'rxjs';
import {DateForm} from '../../../../components/date-picker-field/date-picker-field.component';
import {AttachmentsForm, buildAttachmentsForm} from '../../../../components/files/files.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {KZT_CURRENCY_CODE, MAX_FILES_COUNT} from '../../../../domain/constants';
import {CurrencyRatesHolderService} from '../../../../domain/currency-rates-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {DATE_TIME_FORMATS} from '../../../../infra/date-time.adapter';
import {
    IncidentTransactionViewModel,
    RefundTransactionDialogData,
    RefundTransactionsDialogComponent,
} from './refund-transactions-dialog/refund-transactions-dialog.component';

const REFUND_DESCRIPTION_MAX_LENGTH = 10_000;


@Component({
    selector: 'af-refund-common-information-step',
    standalone: false,
    templateUrl: './refund-common-information-step.component.html',
    styleUrl: './refund-common-information-step.component.scss',
})
export class RefundCommonInformationStepComponent implements OnInit {
    @Input() form: FormGroup<RefundCommonForm>;
    @Input() incidentId: string;
    @Input() refund: RefundResponse;
    @Input() isProcessingBySender: boolean;
    @Output() cancel = new EventEmitter<void>();
    @Output() nextStep = new EventEmitter<void>();
    DATE_TIME_FORMATS = DATE_TIME_FORMATS;
    prosecutionBin: string;
    Spinner = Spinner;
    Validators = Validators;
    BucketName = BucketName;
    MAX_FILES_COUNT = MAX_FILES_COUNT;
    DESCRIPTION_MAX_LENGTH = REFUND_DESCRIPTION_MAX_LENGTH;
    attachmentTypes: ReferenceBookResponse[];
    transactions: IncidentTransaction[] = [];
    filterDictionaryBy = ['REFUND'];
    excludeAttachmentTypesFromDictionary = ['PROSECUTOR_ORDER'];
    excludedRefundTypes: string[] = [];
    isRefundTypeDisabled = false;
    private currencyRates: CurrencyRate[];

    constructor(
        public authHolderService: AuthHolderService,
        private formBuilder: FormBuilder,
        private dialogService: DialogService,
        private localizationService: LocalizationService,
        private dictionaryHolderService: DictionaryHolderService,
        private refundApiService: RefundApiService,
        private currencyRatesHolderService: CurrencyRatesHolderService,
    ) {
    }

    ngOnInit(): void {
        if (!this.authHolderService.hasAccess(['government-user'])) {
            if (this.refund?.type === RefundType.PROSECUTOR_ORDER) {
                this.isRefundTypeDisabled = true;
            } else {
                this.excludedRefundTypes = ['PROSECUTOR_ORDER'];
            }
        }

        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.REGION),
            this.dictionaryHolderService.getReference(ReferenceBookType.ATTACHED_DOC),
            this.refundApiService.getRefundTransactions(this.incidentId).pipe(rxSpinner('transactions')),
        ])
            .subscribe(([regions, attachmentTypes, transactions]) => {
                this.prosecutionBin = findValueInDictionary(regions, this.authHolderService.organizationBin, 'code1');
                this.attachmentTypes = attachmentTypes;
                this.transactions = transactions;
                this.buildForm();
            });

        this.currencyRatesHolderService.getRates()
            .subscribe(currencyRates => {
                this.currencyRates = currencyRates;
                if (this.form.controls.refundAmountInKzt) {
                    this.recalculateAmountInCurrency();
                }
            });
    }

    openTransactionsDialog(): void {
        this.dialogService.openCustomDialogFitContent<RefundTransactionsDialogComponent, IncidentTransactionViewModel[]>(
            RefundTransactionsDialogComponent, <RefundTransactionDialogData>{
                transactions: this.transactions,
                selectedTransactionIds: this.form.controls.transactions.value.length ? this.form.controls.transactions.value
                    .map(transaction => transaction.id) : this.refund?.transactionIds || [],
            })
            .pipe(mergeMap(dialogResult => dialogResult !== undefined ? of(dialogResult) : EMPTY))
            .subscribe(dialogResult => {
                let displayValue = '';
                if (dialogResult.length === 1) {
                    displayValue = this.localizationService.getLocalizedKey('i18n_af_labelIncidentTransactionIndex', {
                        index: (dialogResult[0].index).toString(),
                    });
                } else {
                    displayValue = this.localizationService.getLocalizedKey('i18n_af_placeholderTransactionSelectedCount', {
                            count: dialogResult.length.toString(),
                        },
                    );
                }
                this.form.controls.transactions.setValue(dialogResult);
                this.form.controls.transactionDisplayValue.setValue(displayValue);
            });
    }

    submit(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        this.nextStep.emit();
    }

    private buildForm(): void {
        this.form.addControl('refundType', new FormControl(null, [Validators.required]));
        this.form.controls.refundType.valueChanges.subscribe(value => {
            this.buildCommonForm();
            this.filterDictionaryBy = ['REFUND'];
            this.filterDictionaryBy.push(value);

            if (value === RefundType.PROSECUTOR_ORDER) {
                this.form.removeControl('documentNumber');
                this.form.removeControl('documentDate');
                this.form.addControl('prosecutorOfficeBin', new FormControl(
                    this.form.controls.prosecutorOfficeBin?.value ?? this.refund?.prosecutorOfficeBin,
                    [Validators.required]),
                );
                this.form.addControl('resolutionNumber', new FormControl(
                    this.form.controls.documentNumber?.value ?? this.refund?.kuiNumber,
                    [Validators.required]),
                );
                this.form.addControl('resolutionDate', this.formBuilder.group({
                    date: new FormControl(
                        this.form.getRawValue().resolutionDate?.date
                            ?? (this.refund?.kuiNumberDate ? dayjs(this.refund.kuiNumberDate, DATE_SERVER_FORMAT).toDate() : null),
                        Validators.required,
                    ),
                }));
                this.form.addControl('lawNumber', new FormControl(
                    this.form.controls.lawNumber?.value ?? this.refund?.lawArticle,
                    Validators.required),
                );
                this.form.addControl('lawDate', this.formBuilder.group({
                    date: new FormControl(
                        this.form.getRawValue().lawDate?.date
                            ?? (this.refund?.lawArticleDate ? dayjs(this.refund.lawArticleDate, DATE_SERVER_FORMAT).toDate() : null),
                        Validators.required),
                }));
                this.form.addControl('prosecutorFullName', new FormControl(
                    this.form.controls.prosecutorFullName?.value ?? this.refund?.prosecutorFullName,
                    Validators.required,
                ));
                this.form.addControl('prosecutorJobTitle', new FormControl(
                    this.form.controls.prosecutorJobTitle?.value ?? this.refund?.prosecutorPosition,
                    Validators.required,
                ));
                this.form.addControl('officerFullName', new FormControl(
                    this.form.controls.officerFullName?.value ?? this.refund?.miaFullName,
                    Validators.required));
                this.form.addControl('officerJobTitle', new FormControl(
                    this.form.controls.officerJobTitle?.value ?? this.refund?.miaPosition,
                    Validators.required));
            } else if (value === RefundType.VOLUNTARY) {
                this.form.removeControl('prosecutorOfficeBin');
                this.form.removeControl('resolutionNumber');
                this.form.removeControl('resolutionDate');
                this.form.removeControl('lawNumber');
                this.form.removeControl('lawDate');
                this.form.removeControl('prosecutorFullName');
                this.form.removeControl('prosecutorJobTitle');
                this.form.removeControl('officerFullName');
                this.form.removeControl('officerJobTitle');
                this.form.addControl('documentNumber', new FormControl(
                    this.form.controls.documentNumber?.value ?? this.refund?.kuiNumber,
                    Validators.required));
                this.form.addControl('documentDate', this.formBuilder.group({
                    date: new FormControl(
                        this.form.getRawValue().documentDate?.date
                            ?? (this.refund?.kuiNumberDate ? dayjs(this.refund.kuiNumberDate, DATE_SERVER_FORMAT).toDate() : null),
                        Validators.required,
                    ),
                }));
            }
        });
        if (this.refund) {
            this.form.controls.refundType.setValue(this.refund?.type);
        }
    }

    private buildCommonForm(): void {
        const selectedTransactions = this.transactions.map((transaction, index) => <IncidentTransactionViewModel>{
            ...transaction,
            index: index + 1,
        }).filter(transaction => this.refund?.transactionIds?.includes(transaction.id));
        let displayValue = '';
        if (selectedTransactions.length === 1) {
            displayValue = this.localizationService.getLocalizedKey('i18n_af_labelIncidentTransactionIndex', {
                index: (selectedTransactions[0].index).toString(),
            });
        } else if (selectedTransactions.length > 1) {
            displayValue = this.localizationService.getLocalizedKey('i18n_af_placeholderTransactionSelectedCount', {
                    count: selectedTransactions.length.toString(),
                },
            );
        }

        this.form
            .addControl('transactionDisplayValue', new FormControl(
                this.form.controls.transactionDisplayValue?.value ?? displayValue,
                Validators.required,
            ));
        this.form.addControl('transactions', new FormControl(
            this.form.controls.transactions?.value ?? selectedTransactions ?? [],
            Validators.required,
        ));
        this.form.addControl('grounds', new FormControl(
            this.form.controls.grounds?.value ?? this.refund?.grounds,
            Validators.required,
        ));
        this.form.addControl('refundAmount', new FormControl(
            this.form.controls.refundAmount?.value ?? this.refund?.resolutionAmount,
            Validators.required,
        ));

        this.form.addControl('currency', new FormControl(
            this.form.controls.currency?.value ?? this.refund?.resolutionCurrency,
            Validators.required,
        ));
        this.form.addControl('description', new FormControl(
            this.form.controls.description?.value ?? this.refund?.description,
            Validators.required,
        ));

        this.form.controls.refundAmount.valueChanges.subscribe(() => {
            if (this.form.controls.currency.value && this.form.controls.currency.value !== KZT_CURRENCY_CODE) {
                this.recalculateAmountInCurrency();
            }
        });

        this.form.controls.currency.valueChanges.subscribe(value => {
            if (value && value !== KZT_CURRENCY_CODE) {
                if (!this.form.controls.refundAmountInKzt) {
                    this.form.addControl('refundAmountInKzt', new FormControl(this.refund?.resolutionAmountInKzt, Validators.required));
                }
                this.recalculateAmountInCurrency();
            } else {
                this.form.removeControl('refundAmountInKzt');
            }
        });

        setTimeout(() => {
            this.form.addControl('attachments',
                buildAttachmentsForm(
                    this.formBuilder,
                    this.isProcessingBySender
                        ? []
                        : this.refund?.attachments
                            .filter(attachment => attachment.docType !== 'PROSECUTOR_ORDER')
                        ?? [],
                    this.attachmentTypes,
                    true));
        }, 500);
    }

    private recalculateAmountInCurrency(): void {
        const rateItem = this.currencyRates?.find(rate => rate.code === this.form.getRawValue().currency)?.oneUnitRate;
        if (!rateItem) {
            return;
        }
        const amountInKzt = this.form.getRawValue().refundAmount * rateItem;
        this.form.controls.refundAmountInKzt.setValue(amountInKzt);
    }
}


export interface RefundCommonForm {
    refundType?: FormControl<string>;
    transactionDisplayValue?: FormControl<string>;
    transactions?: FormControl<IncidentTransactionViewModel[]>;
    grounds?: FormControl<string>;
    refundAmount?: FormControl<number>;
    refundAmountInKzt?: FormControl<number>;
    currency?: FormControl<string>;
    description?: FormControl<string>;
    documentNumber?: FormControl<string>;
    documentDate?: FormGroup<DateForm>;
    resolutionNumber?: FormControl<string>;
    resolutionDate?: FormGroup<DateForm>;
    lawNumber?: FormControl<string>;
    lawDate?: FormGroup<DateForm>;
    prosecutorOfficeBin?: FormControl<string>;
    prosecutorFullName?: FormControl<string>;
    prosecutorJobTitle?: FormControl<string>;
    officerFullName?: FormControl<string>;
    officerJobTitle?: FormControl<string>;
    attachments?: FormGroup<AttachmentsForm>;
}
